import { useMemo } from 'react'

import { useModals, useSetState, useShowModal } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext, { PageContextPayload } from '@contexts/pageContext'

import CampaignPortalSyncDetailsModal from '@modals/CampaignPortalSyncDetailsModal'
import EditCampaignPortalModal from '@modals/EditCampaignPortalModal'
import EditCampaignPortalDataModal from '@modals/EditCampaignPortalDataModal'

import type { CampaignModel, CampaignPortalModel, CampaignPortalSyncModel } from '@models/types'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import PageContent from './components/PageContent'

const defaultState = {
  showCampaignPortalSyncDetailsModal: false,
  showEditCampaignPortalModal: false,
  showEditCampaignPortalDataModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CampaignPortalSyncDetailsModal: {
      closeModal: () => setState({ showCampaignPortalSyncDetailsModal: false }),
      fetchLog: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditCampaignPortalModal: {
      closeModal: () => setState({ showEditCampaignPortalModal: false }),
      updateAgency: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCampaignPortal: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditCampaignPortalDataModal: {
      closeModal: () => setState({ showEditCampaignPortalDataModal: false }),
      updateCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCampaignPortalSyncDetailsModal,
    showEditCampaignPortalModal,
    showEditCampaignPortalDataModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showCampaignPortalSyncDetailsModal: (payload: { campaignPortalSync: CampaignPortalSyncModel }) => {
        setModalData('CampaignPortalSyncDetailsModal', payload)
        setState({ showCampaignPortalSyncDetailsModal: true })
      },
      showEditCampaignPortalModal: (payload: { campaignPortal: CampaignPortalModel }) => {
        setModalData('EditCampaignPortalModal', payload)
        setState({ showEditCampaignPortalModal: true })
      },
      showEditCampaignPortalDataModal: (payload: {campaign: CampaignModel}) => {
        setModalData('EditCampaignPortalDataModal', payload)
        setState({ showEditCampaignPortalDataModal: true })
      },
    },
  }), [])

  useShowModal({
    modalKey: 'CampaignPortalSyncDetailsModal',
    options: {
      callbacks: pageContext.callbacks,
    },
  })

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent />

        <CampaignPortalSyncDetailsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CampaignPortalSyncDetailsModal', setState)}
          showModal={showCampaignPortalSyncDetailsModal}
        />

        <EditCampaignPortalModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditCampaignPortalModal', setState)}
          showModal={showEditCampaignPortalModal}
        />

        <EditCampaignPortalDataModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditCampaignPortalDataModal', setState)}
          showModal={showEditCampaignPortalDataModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
